import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, ConfigProvider, message, Modal, Table, Tag} from "antd";
import _ from "lodash";
import React, {useContext, useEffect, useMemo, useState} from "react";
import Organization from "../../domain/Organization";
import {DocumentTitle} from "../DocumentTitle";
import {Link, useNavigate} from "react-router-dom";
import {AppContextContext, SbomProjectServiceContext} from "../../Contexts";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

import * as styles from "./SbomProjectList.module.css";
import * as globalStyles from "../App.module.css";
import {ColumnsType} from "antd/es/table";
import SbomProject from "../../domain/SbomProject";
import SbomProjectVersion from "../../domain/SbomProjectVersion";
import {Permission} from "../../domain/auth/Permission";
import SbomProjectFilter, {createDefaultSbomProjectFilterValues} from "./SbomProjectFilter";
import {FindingSeverity, getEqualOrGreaterSeverities} from "../../domain/FindingSeverity";
import {FormPersister} from "../../sal-ui/FormPersister";
import {renderSbomProjectVersionAnalysisStatistics, SbomProjectVersionFindingStatisticsAction} from "./SbomProjectVersionStatistics";
import VulnerabilitiesLabel from "./VulnerabilitiesLabel";
import {FocusedFindingAnalysisHost} from "./FocusedFindingAnalysisHost";

const PERSISTENT_IDENT = "SbomProjectList";
const FILTER_DEBOUNCE_MS = 500;
const MAX_VISIBLE_VERSIONS = 10;
const RQB_FIELD_GROUP_ID = "groups.id";
const RQB_FIELD_NAME = "name";
const RQB_FIELD_ORGANIZATION_ID = "organization.id";
const RQB_FIELD_SEVERITY = "severity";

function SbomProjectList() {
    const appContext = useContext(AppContextContext);
    const title = "Projects";

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <SbomProjectTable
                    persistentIdent={PERSISTENT_IDENT}
                    showAddButton={true}
                />
            </>
        </DocumentTitle>
    );
}

interface SbomProjectTableProps {
    fixedGroupId?: string;
    persistentIdent?: string;
    showAddButton?: boolean;
}

export function SbomProjectTable(props: SbomProjectTableProps) {
    const appContext = useContext(AppContextContext);
    const sbomProjectService = useContext(SbomProjectServiceContext);
    const navigate = useNavigate();
    const filterFormPersister = useMemo(() => props.persistentIdent ? new FormPersister(props.persistentIdent) : null, [props.persistentIdent]);
    const fixedRqbQuery = useMemo(() => createFixedRqbQuery(props.fixedGroupId), [props.fixedGroupId]);
    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        fixedRqbQuery,
        persistentIdent: props.persistentIdent
    });
    const [sbomProjects, setSbomProjects] = useState<PagedResult<SbomProject>>();
    const [filterValues, setFilterValues] = useState<any>();
    const debouncedOnFinishFilter = useMemo(() => _.debounce(onFinishFilter, FILTER_DEBOUNCE_MS), []);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        restoreFilterValues();
    }, []);

    useEffect(() => () => debouncedOnFinishFilter.cancel(), [debouncedOnFinishFilter]);

    const columns: ColumnsType<SbomProject> = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: renderName
        },
        {
            dataIndex: "versions",
            title: "Versions",
            render: (_, sbomProject) => <SbomProjectVersions sbomProject={sbomProject} versions={sbomProject.versions} onAfterFindingStatusSave={reload}/>
        }
    ];

    if (appContext.user.isSystemAdmin()) {
        columns.push({
            dataIndex: "organization",
            title: "Organization",
            render: renderOrganization
        });
    }

    const hasManageOrg = appContext.user.hasPermission(Permission.SBOM_PROJECT__MANAGE_ORG);
    const showGroupFilter = hasManageOrg && !props.fixedGroupId;

    if (hasManageOrg) {
        columns.push({
            title: "Actions",
            width: 100,
            align: "center",
            className: globalStyles["table-actions"],
            render: renderAction
        });
    }

    return (
        <FocusedFindingAnalysisHost>
            <SbomProjectFilter
                className={styles.filter}
                values={filterValues}
                showGroupFilter={showGroupFilter}
                showOrganizationFilter={appContext.user.isSystemAdmin()}
                persistentStateIdent={props.persistentIdent}
                onChange={values => {
                    setFilterValues(values);

                    debouncedOnFinishFilter(values);
                }}
            />

            <div className={globalStyles["common__top-button-bar"]}>
                <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>

                {hasManageOrg && props.showAddButton &&
                    <Button type={"primary"}
                            icon={<PlusOutlined/>}
                            onClick={() => navigate("/sbom-projects/add")}>Add</Button>
                }
            </div>

            <Table className={styles.table}
                   showSorterTooltip={false}
                   loading={tableHandler.loading}
                   dataSource={sbomProjects?.data}
                   size="middle"
                   onChange={tableHandler.onTableChange}
                   pagination={tableHandler.pagination}
                   onRow={onRow}
                   rowKey="id"
                   columns={columns}
            />
        </FocusedFindingAnalysisHost>
    );

    function renderName(value: any, sbomProject: SbomProject) {
        return <Link to={`/sbom-projects/${sbomProject.id}`}>{value}</Link>;
    }

    function renderOrganization(value: Organization) {
        return <Link to={`/organizations/${value.id}`}>{value.name}</Link>;
    }

    function renderAction(_: any, sbomProject: SbomProject) {
        return (
            <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                    title={"Delete"}
                    onClick={() => {
                        Modal.confirm({
                            content: <>Do you really want to delete project <b>{sbomProject.name}</b>?</>,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onOk: () => onDeleteConfirm(sbomProject)
                        });
                    }}/>
        );
    }

    function onDeleteConfirm(sbomProject: SbomProject) {
        sbomProjectService.delete(sbomProject)
            .then(() => {
                message.success(<>Project <b>{sbomProject.name}</b> successfully deleted.</>);

                reload();
            });
    }

    function reload() {
        return sbomProjectService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setSbomProjects(value);
        });
    }

    function onFinishFilter(values: any) {
        filterFormPersister?.saveState(values);

        tableHandler.onRqbSearchSubmit(createRqbQuery(values));
    }

    function restoreFilterValues() {
        const values = filterFormPersister?.hasState()
            ? filterFormPersister.loadValues()
            : createDefaultSbomProjectFilterValues();

        setFilterValues(values);

        tableHandler.onRqbSearchSubmit(createRqbQuery(values));
    }

    function createRqbQuery(values: any) {
        const rqbQuery = {
            combinator: "and",
            rules: []
        };

        values.name && rqbQuery.rules.push({
            field: RQB_FIELD_NAME,
            operator: "icontains",
            value: values.name
        });

        values.severity && values.severity.length > 0 && rqbQuery.rules.push({
            field: RQB_FIELD_SEVERITY,
            operator: "in",
            value: getEqualOrGreaterSeverities(values.severity as FindingSeverity)
        });

        if (values.groupIds?.length > 0) {
            rqbQuery.rules.push({
                field: RQB_FIELD_GROUP_ID,
                operator: "in",
                value: values.groupIds
            });
        }

        if (appContext.user.isSystemAdmin() && values.organizationIds?.length > 0) {
            rqbQuery.rules.push({
                field: RQB_FIELD_ORGANIZATION_ID,
                operator: "in",
                value: values.organizationIds
            });
        }

        return rqbQuery;
    }

    function onRow(sbomProject: SbomProject) {
        return {
            onDoubleClick: () => navigate(`/sbom-projects/${sbomProject.id}`)
        };
    }
}

function createFixedRqbQuery(groupId?: string) {
    if (!groupId) {
        return undefined;
    }

    return {
        field: RQB_FIELD_GROUP_ID,
        operator: "in",
        value: [groupId]
    };
}

function SbomProjectVersions(props: { sbomProject: SbomProject, versions?: SbomProjectVersion[], onAfterFindingStatusSave?: () => void }) {
    const navigate = useNavigate();
    const [showAllValues, setShowAllValues] = useState(false);
    const versions = props.versions;

    if (versions === undefined) {
        return null;
    }

    const renderTable = (versions: SbomProjectVersion[], footer?: () => React.ReactNode) => (
        <ConfigProvider theme={{components: {Table: {headerBg: 'rgba(0, 0, 0, 0.02)', headerColor: 'rgba(0, 0, 0, 0.65)'}}}}>
            <Table className={styles['versions-table']}
                   showSorterTooltip={false}
                   dataSource={versions}
                   size="small"
                   bordered={false}
                   pagination={false}
                   rowKey="id"
                   footer={footer}
                   onRow={(projectVersion: SbomProjectVersion) => {
                       return {
                           onDoubleClick: (e) => {
                               e.stopPropagation();
                               navigate(`/sbom-projects/${props.sbomProject.id}/${projectVersion.id}`);
                           }
                       };
                   }}
                   columns={[
                       {
                           dataIndex: "name",
                           className: styles['column-name'],
                           title: "Name",
                           render: (_, version) => <>
                               <div>
                                   <Link to={`/sbom-projects/${props.sbomProject.id}/${version.id}`}>{version.name}</Link>

                                   {version?.tags?.length > 0 && <span style={{marginLeft: 8}}>{version.tags.map(value => <Tag key={value}>{value}</Tag>)}</span>}

                                   {version?.licenseViolations?.length > 0 &&
                                       <Tag
                                           style={{marginLeft: 8}}
                                           color={"orange"}
                                           title={"One or more licenses violates project's software license policy."}>
                                           license violation
                                       </Tag>
                                   }
                               </div>

                               {version.projectType &&
                                   <div className={styles.projectType}>{version.projectType}</div>
                               }
                           </>
                       },
                       {
                           dataIndex: "statistics",
                           title: <VulnerabilitiesLabel/>,
                           render: (_, version) => <SbomProjectVersionFindingStatisticsAction
                               projectVersion={version}
                               sbomProject={props.sbomProject}
                               onAfterFindingStatusSave={props.onAfterFindingStatusSave}
                           />
                       },
                       {
                           dataIndex: "analysisStatistics",
                           title: "Findings analysis",
                           width: 100,
                           render: (_, version) => renderSbomProjectVersionAnalysisStatistics(version)
                       }
                   ]}
            />
        </ConfigProvider>
    );

    if (versions.length > MAX_VISIBLE_VERSIONS) {
        if (showAllValues) {
            const footer = () => (
                <Button type={"link"} size={"small"} onClick={() => setShowAllValues(false)}>
                    <i>« show only {MAX_VISIBLE_VERSIONS} versions »</i>
                </Button>
            );

            return renderTable(versions, footer);
        }

        const footer = () => (
            <Button type={"link"} size={"small"} onClick={() => setShowAllValues(true)}>
                <i>« show all {versions.length} versions »</i>
            </Button>
        );

        return renderTable(versions.slice(0, MAX_VISIBLE_VERSIONS), footer);
    }

    return renderTable(versions);
}

export default SbomProjectList;
